import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetCustomGpt, useListCustomGpts } from "features/chat/hooks/use-custom-gpt";
import { ChatInput } from "features/chat/components/ChatInput";
import { Attachment, ButtonIds, initialFeaturesState } from "features/chat/types/chat";
import { useChatStore } from "state/store/chat/use-chat-store";
import { useConfig } from "hooks/use-config";
import { useInviteAssistant } from "features/group-chat/hooks/use-invite";
import { trackEvent } from "analytics/tracker";
import { EVENTS, InitialPromptType } from "analytics/events";
import { InvitationPayload } from "features/group-chat/services/group-services";
import { useModelStore } from "state/store/chat/use-model-store";
import { useIsMobile } from "hooks/use-mobile";
import { Globe, Images, Loader2 } from "lucide-react";
import { Alignment, ButtonTypes, ChatInputButton } from "pages/chat/chat";
import { CustomGptHeader } from "features/chat/components/CustomGptHeader";

export default function CustomGptPage() {
    const { customGptId } = useParams<{ customGptId: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { configurationSettings } = useConfig();

    const { data: assistantList } = useListCustomGpts();
    const { data: fetchedCustomGpt, isLoading: isGptLoading } = useGetCustomGpt(customGptId || "");

    // Use cached data from the list if the individual fetch is still loading or hasn't finished
    const customGpt = fetchedCustomGpt || assistantList?.data?.find(a => a.id === customGptId);

    const inviteAssistantMutation = useInviteAssistant();
    const sendMessage = useChatStore((state: any) => state.sendMessage);
    const setActiveConversationId = useChatStore((state: any) => state.setActiveConversationId);

    const [isFeatureSelected, setIsFeatureSelected] = useState<Record<ButtonIds, boolean>>(initialFeaturesState);
    const [selectedSources, setSelectedSources] = useState<string[]>([]);
    const [isSourcesToolActive, setIsSourcesToolActive] = useState<boolean>(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (customGpt?.modelName) {
            useModelStore.getState().setSelectedModel(customGpt.modelName);
        }
    }, [customGpt?.modelName]);

    // Detach from any previous chat so this landing composer is not bound to it.
    useEffect(() => {
        setActiveConversationId(null);
    }, [customGptId, setActiveConversationId]);

    const inputButtons = useMemo<ChatInputButton[]>(() => [
        {
            id: ButtonIds.SEARCH,
            label: t('SEARCH'),
            icon: <Globe size={16} />,
            onClick: () => {
                setIsFeatureSelected((prev) => ({
                    ...initialFeaturesState,
                    [ButtonIds.SEARCH]: !prev[ButtonIds.SEARCH],
                }));
            },
            type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
            alignment: Alignment.LEFT,
            isDisabled: isFeatureSelected[ButtonIds.CREATE_IMAGE] || selectedSources.length > 0,
            isSelected: isFeatureSelected[ButtonIds.SEARCH],
        },
        {
            id: ButtonIds.CREATE_IMAGE,
            label: t('CREATE_IMAGE'),
            icon: <Images size={16} />,
            onClick: () => {
                setIsFeatureSelected((prev) => ({
                    ...initialFeaturesState,
                    [ButtonIds.CREATE_IMAGE]: !prev[ButtonIds.CREATE_IMAGE],
                }));
            },
            type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
            alignment: Alignment.LEFT,
            isDisabled: selectedSources.length > 0,
            isSelected: isFeatureSelected[ButtonIds.CREATE_IMAGE],
        }
    ], [isMobile, isFeatureSelected, t, selectedSources.length]);

    const handleSendMessage = async (content: string, files: Attachment[]) => {
        if (!customGptId) return;

        setActiveConversationId(null);

        const payload: InvitationPayload = {
            invitationType: 'ADD_USER',
            assistantId: customGpt?.modelName,
            customGptId: customGpt?.id,
        };

        try {
            const response = await inviteAssistantMutation.mutateAsync(payload);

            if (response?.conversationId) {
                const newConversationId = response.conversationId;
                trackEvent(EVENTS.NEW_CHAT_STARTED(newConversationId, InitialPromptType.TEXT));

                await sendMessage(
                    content,
                    configurationSettings,
                    newConversationId,
                    files,
                    isFeatureSelected,
                    false, // isGroupChatParticipants - false since custom gpt is not a group chat
                    undefined, // parentIds
                    undefined, // sources
                    false, // isRegenerate
                    undefined, // originalRegenerationMessageId
                    undefined, // mentions
                    undefined, // parentUserGenerationId
                    false, // isIncognitoChat
                    customGpt?.id // customGptId
                );

                navigate(`/chat/${newConversationId}`, {
                    state: {
                        preservedFeatures: isFeatureSelected,
                        preservedSources: selectedSources,
                        preservedSourcesToolActive: isSourcesToolActive
                    }
                });
            }
        } catch (error) {
            console.error("Failed to start chat with custom GPT:", error);
        }
    };

    // Only show loading if we don't even have the cached data yet
    if (isGptLoading && !customGpt) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!customGpt) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <p className="text-muted-foreground">{t("ASSISTANT_NOT_FOUND")}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full items-center pt-8 md:pt-16 px-4">
            <div className="w-full max-w-3xl flex flex-col items-center gap-6 mb-12 text-center">
                <CustomGptHeader assistant={customGpt} />
                {customGpt.description && (
                    <p className="text-lg text-muted-foreground max-w-2xl mt-2">
                        {customGpt.description}
                    </p>
                )}
            </div>

            <div className="w-full max-w-3xl mt-auto pb-10">
                <ChatInput
                    onSendMessage={handleSendMessage}
                    isLoading={inviteAssistantMutation.isPending}
                    inputButtons={inputButtons}
                    isCurrentResponseGenerating={false}
                    selectedSources={selectedSources}
                    onSourcesChange={setSelectedSources}
                    isFeatureSelected={isFeatureSelected}
                    isSourcesToolActive={isSourcesToolActive}
                    onToggleTool={(toolId) => {
                        setIsFeatureSelected((prev) => ({
                            ...prev,
                            [toolId]: !prev[toolId],
                        }));
                    }}
                    onToggleSourcesTool={() => {
                        setIsSourcesToolActive(!isSourcesToolActive);
                    }}
                    isCancelOnGoing={false}
                    isModelSelectionDisabled={true}
                />
            </div>
        </div>
    );
}
